/**
 * Convert an HTML email into the plain-text alternative part.
 *
 * Mirrors what an email client's text fallback should read like: links become
 * "label <url>", images become "[alt]", list items get bullets, and block
 * elements become line breaks.
 */
export function htmlToPlainText(html: string): string {
  if (!html.trim()) return "";

  const doc = new DOMParser().parseFromString(html, "text/html");

  // Markup that carries no reader-visible text
  doc.querySelectorAll("style, script, head, title, meta, link").forEach((el) =>
    el.remove()
  );

  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href")?.trim() || "";
    const label = a.textContent?.trim() || "";
    if (!href || href.startsWith("#") || href.startsWith("mailto:")) return;
    a.replaceWith(
      doc.createTextNode(label && label !== href ? `${label} <${href}>` : href)
    );
  });

  doc.querySelectorAll("img").forEach((img) => {
    const alt = img.getAttribute("alt")?.trim();
    img.replaceWith(doc.createTextNode(alt ? `[${alt}]` : ""));
  });

  doc.querySelectorAll("li").forEach((li) => {
    li.prepend(doc.createTextNode("- "));
  });

  doc.querySelectorAll("br").forEach((br) => {
    br.replaceWith(doc.createTextNode("\n"));
  });

  // Block-level elements each end a line
  doc
    .querySelectorAll("p, div, tr, li, h1, h2, h3, h4, h5, h6, table, blockquote")
    .forEach((el) => el.after(doc.createTextNode("\n")));

  return (doc.body.textContent || "")
    .replace(/ /g, " ")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Elements a reader sees even when they hold no text of their own. */
const VISIBLE_ELEMENTS =
  "img, hr, iframe, video, audio, picture, svg, canvas, embed, object, " +
  "input, button, textarea, select";

/**
 * True when HTML carries no visible content.
 *
 * Tag-sniffing isn't enough: clearing the editor leaves the document shell
 * behind -- doctype, <head> with its <style>, and the empty layout <table>s --
 * so a template the user has emptied must still read as empty here.
 */
export function isHtmlEmpty(html: string): boolean {
  if (!html.trim()) return true;

  const doc = new DOMParser().parseFromString(html, "text/html");
  // Markup that carries no reader-visible content
  doc.querySelectorAll("head, style, script, template").forEach((el) =>
    el.remove()
  );

  if (doc.body.querySelector(VISIBLE_ELEMENTS)) return false;

  // &nbsp; is spacing, not content
  return (doc.body.textContent || "").replace(/\u00a0/g, " ").trim().length === 0;
}

/**
 * CSS properties a browser writes onto every element when you copy rendered
 * content, none of which change how the email looks. Stripping them turns an
 * unreadable wall of inline styles back into the design's actual styling.
 */
const ALWAYS_NOISE_PROPERTIES = new Set([
  "-webkit-text-stroke-width",
  "-webkit-text-stroke-color",
  "-webkit-text-size-adjust",
  "-webkit-tap-highlight-color",
  "text-size-adjust",
  "text-decoration-color",
  "text-decoration-style",
  "text-decoration-thickness",
  "text-decoration-skip-ink",
  "font-variant-caps",
  "font-variant-ligatures",
  "font-variant-numeric",
  "font-variant-east-asian",
  "font-variant-alternates",
  "font-kerning",
  "font-feature-settings",
  "font-optical-sizing",
  "caret-color",
  // Print-only, never affects how an email renders
  "orphans",
  "widows",
]);

/**
 * Dropped only when they carry the CSS default. Safe because these come from
 * *computed* styles -- an inherited non-default would have been serialised as
 * that value, not as the default.
 *
 * font-weight and font-style are deliberately absent: "font-weight:400" inside
 * a <strong> genuinely means "not bold", so removing it would change rendering.
 */
const NOISE_WHEN_DEFAULT: Record<string, string[]> = {
  "letter-spacing": ["normal"],
  "word-spacing": ["normal", "0px", "0"],
  "white-space": ["normal"],
  "text-transform": ["none"],
};

/**
 * Remove clipboard noise from inline style attributes, leaving real styling
 * (colour, font, size, spacing, borders, alignment) untouched.
 */
export function cleanPastedStyles(html: string): string {
  return html.replace(
    /\sstyle="([^"]*)"/gi,
    (_match: string, declarations: string) => {
      const kept = declarations
        // Split on ";" but not the one ending an HTML entity -- font-family
        // values arrive as &quot;Times New Roman&quot; and a naive split
        // shreds them
        .split(/(?<!&[a-zA-Z#][a-zA-Z0-9]{0,7});/)
        .map((declaration) => declaration.trim())
        .filter(Boolean)
        .filter((declaration) => {
          const colon = declaration.indexOf(":");
          if (colon === -1) return true;

          const property = declaration.slice(0, colon).trim().toLowerCase();
          const value = declaration.slice(colon + 1).trim().toLowerCase();

          // "initial" always means "no change from the default"
          if (value === "initial") return false;
          if (ALWAYS_NOISE_PROPERTIES.has(property)) return false;

          const defaults = NOISE_WHEN_DEFAULT[property];
          return !defaults || !defaults.includes(value);
        });

      return kept.length ? ` style="${kept.join(";")}"` : "";
    }
  );
}

/** Indent HTML so the source view is readable instead of one long line. */
export function formatHtml(html: string): string {
  if (!html.trim()) return "";

  const voidTags = new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "param", "source", "track", "wbr",
  ]);

  const lines = html
    .replace(/>\s*</g, ">\n<")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  let depth = 0;
  return lines
    .map((line) => {
      const isClosing = /^<\//.test(line);
      const tag = line.match(/^<\/?([a-z0-9-]+)/i)?.[1]?.toLowerCase() ?? "";

      if (isClosing) depth = Math.max(0, depth - 1);
      const indented = "  ".repeat(depth) + line;

      const opensBlock =
        /^<[a-z]/i.test(line) &&
        !isClosing &&
        !/\/>$/.test(line) &&
        !voidTags.has(tag) &&
        !line.startsWith("<!") &&
        // A tag that also closes on the same line doesn't open a block
        !/<\/[a-z0-9-]+>$/i.test(line);

      if (opensBlock) depth++;
      return indented;
    })
    .join("\n");
}

/**
 * Does this clipboard text look like HTML source rather than prose?
 * Used to decide whether a paste of raw markup should be parsed instead of
 * shown as literal characters.
 */
export function looksLikeHtmlSource(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith("<")) return false;
  return /<\/(html|body|div|table|td|tr|p|span|a|h[1-6]|ul|ol|li)\s*>/i.test(
    trimmed
  );
}

/** True when the markup is a whole document (an email template), not a fragment. */
export function isFullHtmlDocument(html: string): boolean {
  return /<!doctype\s+html/i.test(html) || /<html[\s>]/i.test(html);
}
